
/**
 * Knowledge Base - Current Features
 * 
 * This file documents the features that are currently implemented in the 3DP Hub platform.
 */

export interface Feature {
  id: string;
  name: string;
  description: string;
  category: string;
  keyComponents?: string[];
}

/**
 * Core functionality and user experience features
 */
export const coreFeatures: Feature[] = [
  {
    id: "part-database",
    name: "3D Printer Parts Database",
    description: "Comprehensive database of 3D printer parts with detailed specifications, compatibility information, and user ratings.",
    category: "core",
    keyComponents: ["Part listings", "Specifications", "Compatibility matrix", "User ratings"]
  },
  {
    id: "advanced-filtering",
    name: "Advanced Filtering System",
    description: "Powerful filtering capabilities allowing users to find parts based on printer compatibility, difficulty level, cost, and more.",
    category: "filtering",
    keyComponents: ["Printer compatibility filter", "Difficulty filter", "Cost filter", "Category filter"]
  },
  {
    id: "responsive-design",
    name: "Mobile-First Responsive Design",
    description: "Optimized user experience across all devices with mobile-first design principles and touch-friendly interfaces.",
    category: "display",
    keyComponents: ["Mobile optimization", "Touch interfaces", "Responsive layouts"]
  },
  {
    id: "dark-mode",
    name: "Dark Mode Support",
    description: "Complete dark mode implementation with automatic theme switching and user preference persistence.",
    category: "display",
    keyComponents: ["Theme switching", "Preference persistence", "Consistent styling"]
  }
];

/**
 * Data organization and metrics features
 */
export const dataFeatures: Feature[] = [
  {
    id: "rating-system",
    name: "User Rating System",
    description: "Community-driven rating system for parts and tutorials with weighted scoring and review capabilities.",
    category: "metrics",
    keyComponents: ["Star ratings", "Review system", "Weighted scoring"]
  },
  {
    id: "difficulty-indicators",
    name: "Installation Difficulty Indicators",
    description: "Clear visual indicators showing the complexity level of part installation and modifications.",
    category: "metrics",
    keyComponents: ["Difficulty levels", "Visual indicators", "Skill requirements"]
  },
  {
    id: "part-categories",
    name: "Organized Part Categories",
    description: "Well-structured categorization system making it easy to browse and discover relevant parts.",
    category: "parts",
    keyComponents: ["Category hierarchy", "Subcategories", "Cross-references"]
  }
];

/**
 * Tutorials and guidance features
 */
export const tutorialFeatures: Feature[] = [
  {
    id: "tutorial-system",
    name: "Comprehensive Tutorial System",
    description: "Step-by-step guides and tutorials for installing parts, troubleshooting, and printer maintenance.",
    category: "tutorials",
    keyComponents: ["Installation guides", "Troubleshooting steps", "Maintenance tutorials"]
  },
  {
    id: "flow-calculator",
    name: "Flow Ratio Calculator",
    description: "Interactive calculator for determining optimal flow ratios in slicer software with support for different calibration methods.",
    category: "tutorials",
    keyComponents: ["Classic method", "YOLO method", "Multi-pass calibration", "Slicer integration guides"]
  }
];

/**
 * User experience and personalization features
 */
export const userFeatures: Feature[] = [
  {
    id: "user-preferences",
    name: "User Preference System",
    description: "Personalized experience with saved filters, preferences, and customizable viewing options.",
    category: "user",
    keyComponents: ["Saved filters", "View preferences", "Personalization"]
  },
  {
    id: "grid-table-views",
    name: "Multiple View Modes",
    description: "Flexible viewing options including both grid and table layouts to suit different user preferences.",
    category: "display",
    keyComponents: ["Grid view", "Table view", "View switching"]
  }
];

/**
 * All current features combined
 */
export const currentFeatures: Feature[] = [
  ...coreFeatures,
  ...dataFeatures,
  ...tutorialFeatures,
  ...userFeatures
];
